import { skipToken } from '@reduxjs/toolkit/query';
import { useMemo } from 'react';
import { useSelector } from 'react-redux';

import { useAuthIsLoaded } from '@/hooks/useAuthIsLoaded';
import type { AccountViewModel } from '@/models/AccountViewModel';
import { useGetAccountsQuery } from '@/store/accountsApi';
import {
  selectExcludedAccounts,
  selectExcludedAccountTypes,
  selectExcludedCategories,
  selectExcludedInstitutions,
} from '@/store/filterSlice';
import { WARootState } from '@/store/store';

interface ExclusionOverrides {
  excludedAccounts?: number[];
  excludedCategories?: number[];
  excludedAccountTypes?: number[];
  excludedInstitutions?: number[];
  institutionsHandledByServer?: boolean;
}

interface EffectiveExclusions {
  excludedAccounts: number[];
  excludedCategories: number[];
  excludedAccountTypes: number[];
  excludedInstitutions: number[];
  isResolvingExclusions: boolean;
  exclusionResolutionFailed: boolean;
}

const uniqueNumbers = (values: number[]) => Array.from(new Set(values));

export function useEffectiveExclusions(
  overrides: ExclusionOverrides = {},
): EffectiveExclusions {
  const userId = useSelector(
    (state: WARootState) => state.appView.UserInfo?.UserId,
  );
  const currentRole = useSelector(
    (state: WARootState) => state.auth.currentRole,
  );
  const authIsLoaded = useAuthIsLoaded();

  const storedExcludedAccounts = useSelector((state: WARootState) =>
    selectExcludedAccounts(state, userId, currentRole),
  );
  const storedExcludedCategories = useSelector((state: WARootState) =>
    selectExcludedCategories(state, userId, currentRole),
  );
  const storedExcludedAccountTypes = useSelector((state: WARootState) =>
    selectExcludedAccountTypes(state, userId, currentRole),
  );
  const storedExcludedInstitutions = useSelector((state: WARootState) =>
    selectExcludedInstitutions(state, userId, currentRole),
  );

  const excludedCategories =
    overrides.excludedCategories ?? storedExcludedCategories;
  const excludedAccountTypes =
    overrides.excludedAccountTypes ?? storedExcludedAccountTypes;
  const excludedInstitutions =
    overrides.excludedInstitutions ?? storedExcludedInstitutions;
  const baseExcludedAccounts =
    overrides.excludedAccounts ?? storedExcludedAccounts;

  const expandsInstitutions = overrides.institutionsHandledByServer !== true;
  const needsAccountExpansion =
    excludedInstitutions.length > 0 && expandsInstitutions;

  const accountsQuery = useGetAccountsQuery(
    authIsLoaded && expandsInstitutions ? { showClosed: false } : skipToken,
  );
  const accountsRecords: AccountViewModel['Records'] | undefined =
    accountsQuery.data?.Records;
  const accountsLoaded = accountsQuery.data !== undefined;
  const accountsFailed = Boolean(accountsQuery.isError);
  const accountsFetching = Boolean(accountsQuery.isFetching);

  const excludedAccounts = useMemo(() => {
    if (!needsAccountExpansion) {
      return baseExcludedAccounts;
    }

    const institutionAccountIds = (accountsRecords ?? [])
      .filter((account) => excludedInstitutions.includes(account.InstitutionId))
      .map((account) => account.AccountId);

    return uniqueNumbers([...baseExcludedAccounts, ...institutionAccountIds]);
  }, [
    accountsRecords,
    baseExcludedAccounts,
    excludedInstitutions,
    needsAccountExpansion,
  ]);

  const isResolvingExclusions =
    needsAccountExpansion &&
    !accountsFailed &&
    (accountsFetching || !accountsLoaded);
  const exclusionResolutionFailed = needsAccountExpansion && accountsFailed;

  return useMemo(
    () => ({
      excludedAccounts,
      excludedCategories,
      excludedAccountTypes,
      excludedInstitutions,
      isResolvingExclusions,
      exclusionResolutionFailed,
    }),
    [
      excludedAccounts,
      excludedCategories,
      excludedAccountTypes,
      excludedInstitutions,
      isResolvingExclusions,
      exclusionResolutionFailed,
    ],
  );
}
